import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Rectangle,
} from "recharts";
import { DadosVendas } from "../Shared/Interfaces/DadosVendas";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { formatToBRL } from "../../Utils/utils";
import colors from "../../theme/colors";


interface SalesChartProps {
  data: DadosVendas | null;
}

const SalesChart2: React.FC<SalesChartProps> = ({ data }) => {
  const produtosVendidos =
    data?.produtosVendidos?.map((produto) => ({
      descricao: produto.descricao,
      totalQuantidade: produto.totalQuantidade,
      valorTotalVendido: produto.valorTotalVendido,
    })) || [];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, boxShadow: 3, bgcolor: 'white' }}>
          <Typography variant="h6" sx={{ color: colors.bluePrimary, mb: 1 }}>
            📊 {label}
          </Typography>
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Quantidade: {payload[0].payload.totalQuantidade}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Valor Total: {formatToBRL(payload[0].value)}
            </Typography>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        {/* Linha 1 - Cards de Resumo */}
        <Grid item xs={12}>
          <Paper sx={{ p: 1, mb: 1 }}>
            <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📊 Resumo das Vendas</h4>
            
            <Grid container spacing={2}>
              {/* Card - Quantidade de Vendas */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ boxShadow: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" sx={{ color: colors.bluePrimary, fontWeight: 'bold', mb: 1 }}>
                      🛒 Quantidade de Vendas
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                      {data?.quantidadeVendas || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Card - Produtos */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ boxShadow: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" sx={{ color: colors.bluePrimary, fontWeight: 'bold', mb: 1 }}>
                      📦 Produtos
                    </Typography>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        <b>Pneus:</b> {data?.quantidadePneus || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        <b>Outros:</b> {data?.quantidadeOutros || 0}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Card - Valor Total Vendas */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ boxShadow: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" sx={{ color: colors.bluePrimary, fontWeight: 'bold', mb: 1 }}>
                      💰 Valor Total Vendas
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                      {formatToBRL(data?.valorTotalVendas ?? 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Card - Valor Médio por Pedido */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ boxShadow: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" sx={{ color: colors.bluePrimary, fontWeight: 'bold', mb: 1 }}>
                      📈 Valor Médio por Pedido
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#FF6F00', fontWeight: 'bold' }}>
                      {formatToBRL(data?.valorMedioPedido ?? 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Linha 2 - Gráfico */}
        <Grid item xs={12}>
          <Paper sx={{ p: 1, mb: 1 }}>
            <h4 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>📊 Gráfico de Vendas por Produtos</h4>
            
            <Paper sx={{ p: 2, height: "100%", boxShadow: 1, border: '1px solid #e0e0e0' }}>
              {produtosVendidos.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={produtosVendidos}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    barCategoryGap="20%"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="descricao" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      fontSize={12}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Valor Total (R$)', 
                        angle: -90, 
                        position: 'insideLeft',
                        fontSize: 12
                      }}
                    />
                    <Tooltip content={CustomTooltip} />
                    <Legend 
                      verticalAlign="top" 
                      height={36}
                      wrapperStyle={{ paddingBottom: '10px' }}
                    />
                    <Bar
                      dataKey="valorTotalVendido"
                      name="Valor Total"
                      fill="#1976D2"
                      activeBar={<Rectangle fill="#1976D2" stroke="#1C1C1C" />}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Nenhum dado disponível para exibir no gráfico
                  </Typography>
                </Box>
              )}
            </Paper>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesChart2;
